import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const WALLETS = {
  metamask: 'metamask',
  phantom: 'phantom',
  coinbase: 'coinbase',
};

export default function Web3Guide() {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({ 1: true });
  const [selectedWallet, setSelectedWallet] = useState(null);

  const toggleSection = (num) => {
    setOpenSections((prev) => ({ ...prev, [num]: !prev[num] }));
  };

  const SectionHeader = ({ num, title }) => (
    <button className="guide-section-header" onClick={() => toggleSection(num)}>
      <div className="guide-section-header-left">
        <span className="deposit-step-number">{num}</span>
        <h3>{title}</h3>
      </div>
      <svg
        className={`wallet-history-chevron ${openSections[num] ? 'open' : ''}`}
        width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>
  );

  return (
    <div className="deposit-guide-page guide-page">
      <h2>Getting Started with Web3</h2>
      <p className="deposit-guide-subtitle">
        New to crypto? No worries. This guide walks you through everything from scratch.
      </p>

      {/* ===== SECTION 1 ===== */}
      <div className="guide-section">
        <SectionHeader num={1} title="What is a wallet?" />
        {openSections[1] && (
          <div className="guide-section-body">
            <p>
              A crypto wallet is like a digital bank account that only you control.
              It lets you hold, send, and receive digital currencies — no bank or company in between.
            </p>
            <p>
              On Finema, your wallet holds <strong>USDC</strong> (a digital dollar worth exactly $1)
              that you use to purchase content. You're always in control of your money.
            </p>
          </div>
        )}
      </div>

      {/* ===== SECTION 2 ===== */}
      <div className="guide-section">
        <SectionHeader num={2} title="Create your wallet" />
        {openSections[2] && (
          <div className="guide-section-body">
            <p>Pick one of these wallet apps. All three work great with Finema.</p>

            <div className="guide-wallet-picker">
              <button
                className={`guide-wallet-option ${selectedWallet === WALLETS.metamask ? 'active' : ''}`}
                onClick={() => setSelectedWallet(WALLETS.metamask)}
              >
                <strong>MetaMask</strong>
                <span className="guide-recommend">Recommended</span>
              </button>
              <button
                className={`guide-wallet-option ${selectedWallet === WALLETS.coinbase ? 'active' : ''}`}
                onClick={() => setSelectedWallet(WALLETS.coinbase)}
              >
                <strong>Coinbase Wallet</strong>
                <span className="guide-recommend">Easiest</span>
              </button>
              <button
                className={`guide-wallet-option ${selectedWallet === WALLETS.phantom ? 'active' : ''}`}
                onClick={() => setSelectedWallet(WALLETS.phantom)}
              >
                <strong>Phantom</strong>
                <span className="guide-recommend">Simple</span>
              </button>
            </div>

            {/* Coinbase Wallet instructions */}
            {selectedWallet === WALLETS.coinbase && (
              <div className="guide-wallet-steps">
                <p className="guide-wallet-why">
                  Coinbase Wallet is the easiest option if you've never used crypto.
                  Clean interface, no technical setup needed.
                </p>
                <div className="deposit-steps">
                  <div className="deposit-step">
                    <span className="deposit-step-number">1</span>
                    <div className="deposit-step-content">
                      <strong>Install the app</strong>
                      <p>
                        <strong>Phone:</strong> Download "Coinbase Wallet" from the App Store or Google Play.
                        <br />
                        <strong>Desktop:</strong> Go to <em>coinbase.com/wallet</em> and install the browser extension.
                      </p>
                      <p className="guide-note">This is different from the Coinbase exchange app. Look for "Coinbase Wallet" (blue icon with a white square).</p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">2</span>
                    <div className="deposit-step-content">
                      <strong>Create a new wallet</strong>
                      <p>Open the app and tap <strong>"Create a new wallet"</strong>. Set up a username if prompted.</p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">3</span>
                    <div className="deposit-step-content">
                      <strong>Save your recovery phrase</strong>
                      <p>You'll see 12 words on screen. Write them down on paper and store them somewhere safe.</p>
                      <div className="guide-warning">
                        Never share your recovery phrase with anyone. Not with Finema, not with support, not with friends. Anyone with these words has full access to your money.
                      </div>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">4</span>
                    <div className="deposit-step-content">
                      <strong>You're set</strong>
                      <p>Coinbase Wallet automatically supports the Base network. No extra setup needed.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MetaMask instructions */}
            {selectedWallet === WALLETS.metamask && (
              <div className="guide-wallet-steps">
                <p className="guide-wallet-why">
                  MetaMask is the most popular crypto wallet in the world.
                  Works on desktop and mobile with thousands of apps.
                </p>
                <div className="deposit-steps">
                  <div className="deposit-step">
                    <span className="deposit-step-number">1</span>
                    <div className="deposit-step-content">
                      <strong>Install MetaMask</strong>
                      <p>
                        <strong>Phone:</strong> Download "MetaMask" from the App Store or Google Play.
                        <br />
                        <strong>Desktop:</strong> Go to <em>metamask.io</em> and click "Download" to install the browser extension.
                      </p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">2</span>
                    <div className="deposit-step-content">
                      <strong>Create a new wallet</strong>
                      <p>Open MetaMask and tap <strong>"Create a new wallet"</strong>. Set a password you'll remember.</p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">3</span>
                    <div className="deposit-step-content">
                      <strong>Save your recovery phrase</strong>
                      <p>MetaMask will show you 12 words. Write them down on paper and keep them somewhere safe.</p>
                      <div className="guide-warning">
                        Never share your recovery phrase with anyone. Not with Finema, not with support, not with friends. Anyone with these words has full access to your money.
                      </div>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">4</span>
                    <div className="deposit-step-content">
                      <strong>Add the Base network</strong>
                      <p>
                        When you connect to Finema, MetaMask will automatically ask you to add the Base network.
                        Just click <strong>"Approve"</strong> when prompted. That's it.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Phantom instructions */}
            {selectedWallet === WALLETS.phantom && (
              <div className="guide-wallet-steps">
                <p className="guide-wallet-why">
                  Phantom is a clean, simple wallet. Originally built for Solana but now works
                  great with Ethereum and Base too.
                </p>
                <div className="deposit-steps">
                  <div className="deposit-step">
                    <span className="deposit-step-number">1</span>
                    <div className="deposit-step-content">
                      <strong>Install Phantom</strong>
                      <p>
                        <strong>Phone:</strong> Download "Phantom" from the App Store or Google Play.
                        <br />
                        <strong>Desktop:</strong> Go to <em>phantom.app</em> and install the browser extension.
                      </p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">2</span>
                    <div className="deposit-step-content">
                      <strong>Create a new wallet</strong>
                      <p>Open Phantom and tap <strong>"Create a new wallet"</strong>. Set up biometric or password lock.</p>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">3</span>
                    <div className="deposit-step-content">
                      <strong>Save your recovery phrase</strong>
                      <p>Phantom will show you 12 words. Write them down on paper and store them safely.</p>
                      <div className="guide-warning">
                        Never share your recovery phrase with anyone. Not with Finema, not with support, not with friends. Anyone with these words has full access to your money.
                      </div>
                    </div>
                  </div>
                  <div className="deposit-step">
                    <span className="deposit-step-number">4</span>
                    <div className="deposit-step-content">
                      <strong>You're set</strong>
                      <p>Phantom supports Base automatically. When you connect to Finema, it will handle the network switch for you.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!selectedWallet && (
              <p className="guide-hint">Pick a wallet above to see the setup steps.</p>
            )}
          </div>
        )}
      </div>

      {/* ===== SECTION 3 ===== */}
      <div className="guide-section">
        <SectionHeader num={3} title="Connect your wallet to Finema" />
        {openSections[3] && (
          <div className="guide-section-body">
            <div className="deposit-steps">
              <div className="deposit-step">
                <span className="deposit-step-number">1</span>
                <div className="deposit-step-content">
                  <strong>Go to your Wallet page</strong>
                  <p>On Finema, tap <strong>Web3 Wallet</strong>, then click the <strong>"Connect Wallet"</strong> button.</p>
                </div>
              </div>
              <div className="deposit-step">
                <span className="deposit-step-number">2</span>
                <div className="deposit-step-content">
                  <strong>Choose your wallet</strong>
                  <p>A popup will appear showing wallet options. Select the wallet you installed (MetaMask, Coinbase Wallet, or Phantom).</p>
                </div>
              </div>
              <div className="deposit-step">
                <span className="deposit-step-number">3</span>
                <div className="deposit-step-content">
                  <strong>Approve the connection</strong>
                  <p>Your wallet app will ask you to confirm. Tap <strong>"Connect"</strong>. This is safe — it only lets Finema see your address, nothing else.</p>
                </div>
              </div>
              <div className="deposit-step">
                <span className="deposit-step-number">4</span>
                <div className="deposit-step-content">
                  <strong>Done</strong>
                  <p>Your wallet address will appear on the page. You're connected.</p>
                </div>
              </div>
            </div>

            <div className="guide-troubleshoot">
              <strong>Nothing happening?</strong>
              <ul>
                <li>Make sure your wallet extension is installed and unlocked</li>
                <li>Try refreshing the page</li>
                <li>On mobile, open Finema from inside your wallet app's built-in browser</li>
                <li>Disable other wallet extensions that might conflict</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* ===== SECTION 4 ===== */}
      <div className="guide-section">
        <SectionHeader num={4} title="Add funds to your account" />
        {openSections[4] && (
          <div className="guide-section-body">
            <div className="guide-path-card guide-path-primary">
              <h4>Easiest: Deposit with card</h4>
              <div className="deposit-steps">
                <div className="deposit-step">
                  <span className="deposit-step-number">1</span>
                  <div className="deposit-step-content">
                    <strong>Go to Normal Wallet</strong>
                    <p>On the Wallet page, open <strong>Normal Wallet</strong> and tap <strong>"Deposit with card"</strong>.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <span className="deposit-step-number">2</span>
                  <div className="deposit-step-content">
                    <strong>Choose a provider and pay</strong>
                    <p>Select <strong>Coinbase Onramp</strong>, enter the amount, and pay with your credit or debit card.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <span className="deposit-step-number">3</span>
                  <div className="deposit-step-content">
                    <strong>Funds appear automatically</strong>
                    <p>The USDC is deposited into your Finema balance within minutes. No extra steps needed.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="guide-path-card">
              <h4>Alternative: Buy on an exchange</h4>
              <p>If you already have crypto or prefer using an exchange:</p>
              <div className="deposit-steps">
                <div className="deposit-step">
                  <span className="deposit-step-number">1</span>
                  <div className="deposit-step-content">
                    <strong>Buy USDC</strong>
                    <p>On Binance, Coinbase, or any exchange, buy USDC.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <span className="deposit-step-number">2</span>
                  <div className="deposit-step-content">
                    <strong>Send to your wallet</strong>
                    <p>Withdraw USDC to your wallet address using the <strong>Base network</strong>. Double-check the address and network before confirming.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <span className="deposit-step-number">3</span>
                  <div className="deposit-step-content">
                    <strong>Deposit in Finema</strong>
                    <p>On the Web3 Wallet page, use the <strong>"Deposit USDC in Finema"</strong> panel to move your USDC into the platform.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== SECTION 5 ===== */}
      <div className="guide-section">
        <SectionHeader num={5} title="You're ready!" />
        {openSections[5] && (
          <div className="guide-section-body">
            <p>
              That's it. You can now browse Finema and purchase any content you like.
              Just click <strong>"Buy"</strong> on any film, course, or documentary — the payment
              happens instantly from your balance.
            </p>
            <p>
              <strong>How payments work:</strong> When you buy content, 90% goes directly to the creator
              and 10% to the platform. Every payment is recorded on the blockchain, so it's fully
              transparent and verifiable.
            </p>
            <p>
              Once you own content, it's yours forever. You can watch it anytime from your Library.
            </p>
          </div>
        )}
      </div>

      {/* Support */}
      <div className="guide-support">
        <p>Stuck or have questions? Reach out to us at <a href="mailto:support@finema.io">support@finema.io</a></p>
      </div>

      <button className="btn btn-primary deposit-back-btn" onClick={() => navigate('/wallet')}>
        Back to my wallet
      </button>
    </div>
  );
}
